import * as Sentry from "@sentry/react";
import posthog from 'posthog-js';

/**
 * Custom error class for Supabase errors with additional context
 * 
 * @example
 * ```typescript
 * // This will create a proper Error object with Supabase details
 * const error = handleSupabaseError(supabaseError, 'fetch_user_data');
 * 
 * // The error will have these properties:
 * // - error.message: The Supabase error message
 * // - error.code: Supabase error code (e.g., '23505' for unique constraint violation)
 * // - error.details: Additional error details from Supabase
 * // - error.hint: Supabase's suggested fix
 * // - error.context: Our custom context ('fetch_user_data')
 * 
 * // When captured with captureAppException, Sentry will show:
 * // - The error message in the main error view
 * // - Supabase details in the "Extra" section
 * // - PostHog will receive flattened properties for analytics
 * ```
 */
export class SupabaseError extends Error {
  public readonly code?: string;
  public readonly details?: string;
  public readonly hint?: string;
  public readonly context?: string;

  constructor(message: string, supabaseError?: any, context?: string) {
    super(message);
    this.name = 'SupabaseError';
    
    // Preserve the stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, SupabaseError);
    }

    // Attach Supabase error details
    if (supabaseError?.code) this.code = supabaseError.code;
    if (supabaseError?.details) this.details = supabaseError.details;
    if (supabaseError?.hint) this.hint = supabaseError.hint;
    if (context) this.context = context;
  }
}

/**
 * Creates a proper Error object from a Supabase error with additional context
 * 
 * @example
 * ```typescript
 * // This will create a proper Error object with Supabase details
 * const error = createSupabaseError(supabaseError, 'fetch_user_data');
 * 
 * // The error will have these properties:
 * // - error.message: The Supabase error message
 * // - error.code: Supabase error code (e.g., '23505' for unique constraint violation)
 * // - error.details: Additional error details from Supabase
 * // - error.hint: Supabase's suggested fix
 * // - error.context: Our custom context ('fetch_user_data')
 * 
 * // When captured with captureAppException, Sentry will show:
 * // - The error message in the main error view
 * // - Supabase details in the "Extra" section
 * // - PostHog will receive flattened properties for analytics
 * ```
 */
export const createSupabaseError = (supabaseError: any, context?: string): Error => {
  // Extract the error message
  const message = supabaseError?.message || 'Unknown Supabase error occurred';
  
  // Create a proper SupabaseError object
  return new SupabaseError(message, supabaseError, context);
};

/**
 * Captures an exception with Sentry and PostHog, with proper handling of custom error properties
 *
 * @param error The error object to capture.
 * @param context Optional additional data to send with the error.
 *                For Sentry, this is used as the captureContext.
 *                For PostHog, this is sent as additional properties.
 */
export const captureAppException = (error: any, context?: Record<string, any>): void => {
  // Prepare enhanced context with error details
  const enhancedContext = { ...context };
  
  // If it's our custom SupabaseError, extract the additional properties
  if (error instanceof SupabaseError) {
    enhancedContext.supabaseError = {
      code: error.code,
      details: error.details,
      hint: error.hint,
      context: error.context
    };
  }

  // Capture with Sentry
  try {
    Sentry.captureException(error, {
      ...context,
      extra: {
        ...context?.extra,
        ...enhancedContext.supabaseError && { supabaseError: enhancedContext.supabaseError }
      }
    });
  } catch (sentryError) {
    console.error("Failed to capture exception with Sentry:", sentryError);
  }

  // Capture with PostHog
  try {
    // PostHog expects a simple object for additional properties
    const posthogContext = {
      ...context,
      ...enhancedContext.supabaseError && { 
        supabaseCode: error.code,
        supabaseDetails: error.details,
        supabaseHint: error.hint,
        errorContext: error.context
      }
    };
    
    posthog.captureException(error, posthogContext);
  } catch (posthogError) {
    console.error("Failed to capture exception with PostHog:", posthogError);
  }
}; 